import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function Tfsa() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="tax-free-savings-account-tfsa" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Tax-Free Savings Account (TFSA)</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Grow Your Savings and Investments Completely Tax-Free
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is a TFSA? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is a TFSA?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                The Tax-Free Savings Account (TFSA) is a registered savings and investment account available to Canadians aged 18 and older with a valid Social Insurance Number (SIN). Its primary advantage is that any investment income earned within the account (interest, dividends, capital gains) and any withdrawals made from the account are completely tax-free.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Contributions are made with after-tax dollars (meaning you don't get a tax deduction for contributing), but the tax-free growth and withdrawals make it an incredibly powerful tool for achieving a wide range of short-term and long-term financial goals.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Eligibility & Contribution Room:</strong> Any Canadian resident 18+ with a SIN accumulates TFSA contribution room each year. The government sets an annual limit (e.g., $7,000 in 2024). Unused room carries forward indefinitely. You can check your available room via your CRA My Account.
                </li>
                <li>
                  <strong>Making Contributions:</strong> You contribute money (up to your available limit) into a TFSA account opened at a financial institution.
                </li>
                <li>
                  <strong>Investing Funds:</strong> Within the TFSA, you can hold various qualified investments, such as cash, GICs, bonds, stocks, mutual funds, and ETFs.
                </li>
                <li>
                  <strong>Tax-Free Growth:</strong> All investment earnings generated inside the TFSA are sheltered from tax.
                </li>
                <li>
                  <strong>Tax-Free Withdrawals:</strong> You can withdraw funds from your TFSA at any time, for any reason, without paying tax on the withdrawn amount (including contributions and growth).
                </li>
                 <li>
                  <strong>Contribution Room Restoration:</strong> The full amount of any withdrawal made in a calendar year is added back to your TFSA contribution room starting in the *next* calendar year.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Completely Tax-Free Growth:</strong> Investment income and capital gains are never taxed.</li>
                <li><strong>Tax-Free Withdrawals:</strong> Access your money anytime without tax consequences.</li>
                <li><strong>Flexibility:</strong> Suitable for various goals – emergency fund, car purchase, down payment, vacation, retirement savings supplement.</li>
                <li><strong>Contribution Room Carry-Forward:</strong> Unused room accumulates over the years.</li>
                <li><strong>Withdrawal Room Restoration:</strong> Withdrawn amounts can be re-contributed in future years.</li>
                <li><strong>No Impact on Income-Tested Benefits:</strong> TFSA withdrawals do not count as income and won't affect benefits like OAS or GIS.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Contribution Limits:</strong> Over-contributing results in penalties (1% per month on the excess amount). Always track your contribution room.</li>
                <li><strong>No Tax Deduction:</strong> Contributions are made with after-tax money and are not tax-deductible like RRSP contributions.</li>
                <li><strong>Investment Losses:</strong> While gains are tax-free, investment losses within a TFSA cannot be used to offset capital gains outside the TFSA.</li>
                <li><strong>Withdrawal Timing & Re-contribution:</strong> Withdrawn amounts are only added back to your room in the *next* calendar year. Re-contributing in the same year could lead to over-contribution if you've already maxed out your current year's limit.</li>
                <li><strong>Qualified Investments:</strong> Only certain types of investments are permitted.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is a TFSA Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">The TFSA is a valuable tool for virtually every Canadian adult, regardless of income level. It's particularly beneficial for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Saving for short-to-medium term goals where tax-free access to funds is important.</li>
                <li>Supplementing retirement savings alongside RRSPs.</li>
                <li>Lower-income earners who may not benefit as much from an RRSP tax deduction.</li>
                <li>Building an emergency fund that earns tax-free interest.</li>
                <li>Anyone looking to shelter investment growth from taxes.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Maximize Your Tax-Free Savings Potential</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                The TFSA is a cornerstone of smart financial planning in Canada. Let's discuss how to best utilize your TFSA contribution room to achieve your goals.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Open or Review Your TFSA
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
